/**
 * Notification system constants
 * Centralized configuration for consistent behavior
 */

import { ErrorHandler, type AppError } from '@/utils/errorHandling'

// Pagination constants
export const UI_PAGE_SIZE = 5 // Number of notifications shown in UI tray
export const BULK_PAGE_SIZE = 100 // Page size for bulk operations (dismiss all, etc.)

// Timing constants
export const DISMISS_FILTER_DURATION = 10_000 // 10 seconds - how long to filter dismissed notifications
export const POLLING_PAUSE_DURATION = 1_000 // 1 second - pause after mutations complete
export const CLEANUP_INTERVAL = 10_000 // 10 seconds - how often to clean up expired dismiss filters

// API configuration
export const REQUEST_TIMEOUT = 10_000 // 10 seconds - timeout for individual notification requests
export const BULK_CONCURRENCY_LIMIT = 6 // Maximum concurrent dismiss requests
export const MAX_RETRY_ATTEMPTS = 3 // Maximum number of retry attempts for API calls

// HTTP status code ranges
export const HTTP_CLIENT_ERROR_START = 400
export const HTTP_SERVER_ERROR_START = 500

// UI thresholds
export const MIN_VISIBLE_NOTIFICATIONS_THRESHOLD = 2 // Minimum visible notifications before refetch

// Time conversion constants (in seconds)
export const SECONDS_IN_MINUTE = 60
export const SECONDS_IN_HOUR = 3600
export const SECONDS_IN_DAY = 86400
export const SECONDS_IN_WEEK = 604800

// Animation durations (in ms)
export const ANIMATION_DURATION_FAST = 200 // 0.2s for quick transitions
export const ANIMATION_STAGGER_DELAY = 50 // Delay between staggered animations

// Icon sizes (in pixels)
export const ICON_SIZE_SMALL = 12
export const ICON_SIZE_MEDIUM = 20

// Polling intervals (in ms)
export const POLLING_INTERVAL_ACTIVE = 15000 // 15 seconds when tab is active
// Lower hidden interval for more responsive background updates
export const POLLING_INTERVAL_HIDDEN = 20000 // 20 seconds when tab is hidden
export const POLLING_INTERVAL_SAVE_DATA = 60000 // 60 seconds in save data mode
export const POLLING_INTERVAL_2G = 90000 // 90 seconds on 2g connections
export const POLLING_JITTER_MAX = 1000 // Max random jitter for polling

// Query configuration
export const QUERY_STALE_TIME = 15000 // 15 seconds
export const QUERY_GC_TIME = 300000 // 5 minutes

// Error messages
const ERROR_MESSAGES = {
    DISMISS_FAILED: 'Failed to dismiss notification',
    DISMISS_ALL_FAILED: 'Failed to dismiss notifications',
    FETCH_FAILED: 'Failed to load notifications',
    COUNT_FAILED: 'Failed to get notification count',
} as const

/**
 * Consistent error message helper for notifications
 * Uses the app-wide ErrorHandler with notification-specific fallbacks
 */
export const getNotificationErrorMessage = (error: AppError, context: keyof typeof ERROR_MESSAGES): string => {
    // Use the global error handler first
    const message = ErrorHandler.getUserFriendlyMessage(error)

    // If it's a generic message, provide notification-specific context
    if (message === 'An error occurred' || message === 'An unexpected error occurred') {
        return ERROR_MESSAGES[context]
    }

    return message
}

export const NOTIFICATION_CONSTANTS = {
    UI_PAGE_SIZE,
    BULK_PAGE_SIZE,
    DISMISS_FILTER_DURATION,
    POLLING_PAUSE_DURATION,
    CLEANUP_INTERVAL,
    REQUEST_TIMEOUT,
    BULK_CONCURRENCY_LIMIT,
    ERROR_MESSAGES,
    MAX_RETRY_ATTEMPTS,
    HTTP_CLIENT_ERROR_START,
    HTTP_SERVER_ERROR_START,
    MIN_VISIBLE_NOTIFICATIONS_THRESHOLD,
    SECONDS_IN_MINUTE,
    SECONDS_IN_HOUR,
    SECONDS_IN_DAY,
    SECONDS_IN_WEEK,
    ANIMATION_DURATION_FAST,
    ANIMATION_STAGGER_DELAY,
    ICON_SIZE_SMALL,
    ICON_SIZE_MEDIUM,
    POLLING_INTERVAL_ACTIVE,
    POLLING_INTERVAL_HIDDEN,
    POLLING_INTERVAL_SAVE_DATA,
    POLLING_INTERVAL_2G,
    POLLING_JITTER_MAX,
    QUERY_STALE_TIME,
    QUERY_GC_TIME,
} as const
